/**
 * MarketplaceRegister — AWS Marketplace → Beacon registration flow.
 *
 * AWS redirects the customer to:
 *   https://your-app.com/marketplace/register?token=<x-amzn-marketplace-token>
 *
 * This page:
 *  1. Reads the token from the query string
 *  2. Collects the customer's email
 *  3. POSTs to /api/v1/marketplace/register
 *  4. Confirms the subscription and sends the customer to sign in
 */
import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle, AlertCircle, Loader2, ExternalLink } from 'lucide-react'
import api from '../utils/api'
import { BeaconLogo, BeaconWordmark } from '@/components/BeaconBrand'

type Step = 'loading' | 'pricing' | 'form' | 'registering' | 'success' | 'error'

const MARKETPLACE_SEARCH_URL = 'https://aws.amazon.com/marketplace/search/results?searchTerms=Beacon'

const PUBLIC_TIERS = [
  {
    name: 'Free',
    price: '$0',
    cadence: 'month',
    accounts: '1 AWS account',
    actions: '250 BeaCon actions/month',
    tasks: '5 scheduled tasks',
  },
  {
    name: 'Starter',
    price: '$49',
    cadence: 'month',
    accounts: '1 AWS account',
    actions: '2,000 BeaCon actions/month',
    tasks: '25 scheduled tasks',
  },
  {
    name: 'Professional',
    price: '$199',
    cadence: 'month',
    accounts: '3 AWS accounts',
    actions: '15,000 BeaCon actions/month',
    tasks: '100 scheduled tasks',
  },
]

const TIER_LABELS: Record<string, { label: string; color: string; description: string }> = {
  free: {
    label: 'Free',
    color: 'text-gray-400',
    description: '1 AWS account · 250 actions/month · 5 scheduled tasks',
  },
  starter: {
    label: 'Starter',
    color: 'text-blue-400',
    description: '1 AWS account · 2,000 actions/month · 25 scheduled tasks',
  },
  professional: {
    label: 'Professional',
    color: 'text-teal-400',
    description: '3 AWS accounts · 15,000 actions/month · 100 scheduled tasks',
  },
  enterprise: {
    label: 'Enterprise',
    color: 'text-amber-400',
    description: 'Private offer · up to 25 accounts · 500,000 actions/month',
  },
}

export default function MarketplaceRegister() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [step, setStep] = useState<Step>('loading')
  const [token, setToken] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [tier, setTier] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const t = searchParams.get('token') || searchParams.get('x-amzn-marketplace-token')
    if (!t) {
      setStep('pricing')
      return
    }
    setToken(t)
    setStep('form')
  }, [searchParams])

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token || !email.trim()) return

    setStep('registering')
    setError(null)

    try {
      const { data } = await api.post('/api/v1/marketplace/register', {
        token,
        email: email.trim(),
      })

      setTier(data.subscription_tier)
      setStep('success')

      setTimeout(() => navigate('/login'), 3000)
    } catch (err: any) {
      const detail = err.response?.data?.detail
      setError(
        typeof detail === 'string'
          ? detail
          : 'Registration failed. Please try again or contact BeaCon support at support@dynamoconsulting.solutions'
      )
      setStep('form')
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">

        {/* Brand — same Beacon B mark + BeaCon gold-circle wordmark as Login/Layout */}
        <div className="flex flex-col items-center gap-3 mb-8 text-center">
          <div className="w-fit">
            <BeaconLogo size={84} />
          </div>
          <BeaconWordmark className="text-3xl font-extrabold" textClassName="text-white" />
          <p className="text-xs text-gray-500 max-w-sm leading-relaxed">
            BeaCon - Personal AWS Assistant
          </p>
        </div>

        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 shadow-2xl">

          {/* AWS Marketplace badge */}
          <div className="mb-6 px-3 py-3 bg-amber-900/20 border border-amber-700/40 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <ExternalLink size={14} className="text-amber-400 flex-shrink-0" />
              <span className="text-xs text-amber-300 font-medium">AWS Marketplace subscription</span>
            </div>
            <ul className="text-[11px] text-amber-200/90 space-y-1.5 leading-snug list-disc list-inside">
              <li>Billing runs through your AWS account</li>
              <li>Usage is metered for your BeaCon plan</li>
              <li>After activate, land in BeaCon home (chat + ledger)</li>
            </ul>
          </div>

          {/* ── Loading ── */}
          {step === 'loading' && (
            <div className="text-center py-8">
              <Loader2 size={32} className="animate-spin text-blue-400 mx-auto mb-3" />
              <p className="text-gray-400">Verifying your Marketplace token…</p>
            </div>
          )}

          {/* ── Direct visit / pricing ── */}
          {step === 'pricing' && (
            <div className="py-1">
              <h1 className="text-xl font-bold text-white mb-2"><BeaconWordmark className="font-bold" textClassName="text-white" /> pricing</h1>
              <p className="text-sm text-gray-400 mb-5">
                Subscribe through AWS Marketplace to activate BeaCon billing and registration.
              </p>

              <div className="space-y-3 mb-6">
                {PUBLIC_TIERS.map(plan => (
                  <div key={plan.name} className="bg-gray-800/70 border border-gray-700 rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h2 className="text-sm font-semibold text-white">{plan.name}</h2>
                        <p className="text-xs text-gray-400 mt-1">{plan.accounts}</p>
                        <p className="text-xs text-gray-400">{plan.actions}</p>
                        <p className="text-xs text-gray-400">{plan.tasks}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-white">{plan.price}</p>
                        <p className="text-[11px] text-gray-500">/{plan.cadence}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <a
                href={MARKETPLACE_SEARCH_URL}
                target="_blank"
                rel="noreferrer"
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
              >
                View on AWS Marketplace
                <ExternalLink size={15} />
              </a>

              <p className="mt-4 text-xs text-gray-500 text-center leading-relaxed">
                If you already subscribed, return from AWS Marketplace so this page receives the registration token.
              </p>
            </div>
          )}

          {/* ── Error ── */}
          {step === 'error' && (
            <div className="text-center py-4">
              <AlertCircle size={40} className="text-red-400 mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-white mb-2">Registration Error</h2>
              <p className="text-sm text-gray-400 mb-6">{error}</p>
              <a
                href={MARKETPLACE_SEARCH_URL}
                target="_blank"
                rel="noreferrer"
                className="text-blue-400 hover:text-blue-300 text-sm underline"
              >
                Return to AWS Marketplace →
              </a>
            </div>
          )}

          {/* ── Form ── */}
          {(step === 'form' || step === 'registering') && (
            <>
              <h1 className="text-xl font-bold text-white mb-2">Complete your registration</h1>
              <p className="text-sm text-gray-400 mb-6">
                You&apos;re one step away from activating BeaCon. Enter the email address
                you&apos;d like to use to log in.
              </p>

              {error && (
                <div className="flex items-start gap-2 p-3 mb-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  {error}
                </div>
              )}

              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Email address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@yourcompany.com"
                    required
                    disabled={step === 'registering'}
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 disabled:opacity-50"
                  />
                </div>

                <button
                  type="submit"
                  disabled={step === 'registering' || !email.trim()}
                  className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
                >
                  {step === 'registering' ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Activating subscription…
                    </>
                  ) : (
                    'Activate BeaCon'
                  )}
                </button>
              </form>

              <p className="mt-4 text-xs text-gray-500 text-center">
                Your subscription is managed through AWS Marketplace. By activating,
                you agree to the{' '}
                <a href="/terms" className="text-gray-400 hover:text-gray-200 underline">Terms of Service</a>.
              </p>
            </>
          )}

          {/* ── Success ── */}
          {step === 'success' && (
            <div className="text-center py-4">
              <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">You&apos;re all set!</h2>

              {tier && TIER_LABELS[tier] && (
                <div className="mb-4 px-4 py-3 bg-gray-800 rounded-xl">
                  <p className="text-sm text-gray-400 mb-1">Your plan</p>
                  <p className={`text-lg font-bold ${TIER_LABELS[tier].color}`}>
                    {TIER_LABELS[tier].label}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{TIER_LABELS[tier].description}</p>
                </div>
              )}

              <p className="text-sm text-gray-400 mb-6">
                Your BeaCon subscription is active. Sign in to open BeaCon home
                (chat + ledger). Redirecting you to the login page…
              </p>

              <button
                onClick={() => navigate('/login')}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium text-sm transition-colors"
              >
                Go to Login →
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-600 mt-6">
          Need BeaCon help?{' '}
          <a href="mailto:support@dynamoconsulting.solutions" className="text-gray-400 hover:text-gray-200">
            support@dynamoconsulting.solutions
          </a>
        </p>
      </div>
    </div>
  )
}
